import { useEffect, useMemo, useState } from "react";
import { Marker, Polyline, Polygon } from "react-leaflet";
import L from "leaflet";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import LeafletMap, { FitBounds, pointInPolygon } from "@/components/map/LeafletMap";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { MapPin, Star, Navigation } from "lucide-react";
import { TrailheadPinDialog, AMENITY_TYPES } from "@/components/admin/TrailheadPinDialog";
import { TYPE_LABEL } from "@/lib/directions";

const colorOf = (t) => (AMENITY_TYPES.find((x) => x.value === t) || {}).color || "#94A3B8";
const pinIcon = (a) => L.divIcon({
  className: "", iconSize: [22, 22], iconAnchor: [11, 11],
  html: `<div style="width:22px;height:22px;border-radius:50%;background:${colorOf(a.type)};opacity:${a.status === "pending" || a.status === "rejected" ? 0.55 : 1};border:3px ${a.status === "pending" ? "dashed" : "solid"} ${a.is_primary ? "#FDE047" : "#fff"};box-shadow:0 1px 4px rgba(0,0,0,.5)"></div>`,
});
const STATUS_BADGE = { pending: ["Awaiting approval", "bg-amber-500/15 text-amber-500"], rejected: ["Not approved", "bg-destructive/15 text-destructive"] };

export default function TrailheadsAdminPanel({ mode = "admin", onPendingCount }) {
  const partner = mode === "partner";
  const base = partner ? "/partner/amenities" : "/admin/amenities";
  const canEdit = (p) => !partner || !p.id || p.mine;
  const [paths, setPaths] = useState([]);
  const [pathStates, setPathStates] = useState({});
  const [stateF, setStateF] = useState("all");
  const [pid, setPid] = useState("");
  const [pins, setPins] = useState([]);
  const [riderView, setRiderView] = useState([]);
  const [edit, setEdit] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pending, setPending] = useState([]);
  const path = paths.find((p) => p.id === pid);
  const loadPending = () => {
    if (partner) return;
    api.get("/admin/amenities/pending").then(({ data }) => { setPending(data || []); onPendingCount?.((data || []).length); }).catch(() => {});
  };
  useEffect(() => { loadPending(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const jumpTo = (p) => { setStateF("all"); setPid(p.bike_path_id); };

  useEffect(() => {
    api.get(partner ? "/partner/amenity-paths" : "/bike-paths", { params: partner ? {} : { lite: 1 } }).then(({ data }) => { setPaths(data || []); if (data?.[0]) setPid(data[0].id); });
    if (!partner) api.get("/admin/bike-path-states").then(({ data }) => setPathStates(data || {})).catch(() => {});
  }, []);
  const states = useMemo(() => [...new Set(Object.values(pathStates))].sort(), [pathStates]);
  const shownPaths = useMemo(() => paths.filter((p) => stateF === "all" || (pathStates[p.id] || "none") === stateF), [paths, pathStates, stateF]);
  const pickState = (s) => {
    setStateF(s);
    const list = paths.filter((p) => s === "all" || (pathStates[p.id] || "none") === s);
    if (!list.some((p) => p.id === pid)) setPid(list[0]?.id || "");
  };
  const load = async () => {
    if (!pid) return;
    const [{ data: a }, { data: th }] = await Promise.all([
      api.get(base, { params: { bike_path_id: pid } }),
      api.get(`/public/paths/${pid}/trailheads`).catch(() => ({ data: { access_points: [] } })),
    ]);
    setPins(a || []); setRiderView(th.access_points || []);
  };
  useEffect(() => { load(); }, [pid]); // eslint-disable-line react-hooks/exhaustive-deps

  const lines = useMemo(() => (path?.lines?.length ? path.lines : (path?.centerline?.length >= 2 ? [path.centerline] : [])), [path]);
  const zones = useMemo(() => (partner ? (path?.zones || []).filter((z) => z.polygon?.length >= 3) : []), [partner, path]);
  const inZone = (lat, lng) => !partner || zones.some((z) => pointInPolygon(lat, lng, z.polygon));
  const mapClick = (lat, lng) => {
    if (!pid) return;
    if (!inZone(lat, lng)) { toast.error("Pins can only be placed inside your assigned zone (shaded on the map)."); return; }
    setEdit({ lat, lng });
  };
  const bounds = useMemo(() => [...lines.flat(), ...pins.map((p) => [p.lat, p.lng]), ...(partner ? (path?.zones || []).flatMap((z) => z.polygon || []) : [])], [lines, pins, partner, path]);

  const save = async (f) => {
    setSaving(true);
    const body = { type: f.type, name: f.name.trim(), lat: f.lat, lng: f.lng, bike_path_id: pid, notes: f.notes || "", is_primary: !!f.is_primary };
    try {
      if (f.id) await api.put(`${base}/${f.id}`, body); else await api.post(base, body);
      toast.success(partner ? "Pin sent to Velopaths for approval" : "Pin saved"); setEdit(null); await load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Couldn't save pin"); }
    finally { setSaving(false); }
  };
  const remove = async (f) => {
    setSaving(true);
    try { await api.delete(`${base}/${f.id}`); toast.success("Pin deleted"); setEdit(null); await load(); }
    catch { toast.error("Couldn't delete pin"); } finally { setSaving(false); }
  };
  const move = async (p, ll) => {
    if (!inZone(ll.lat, ll.lng)) { toast.error("Pins can only be placed inside your assigned zone."); await load(); return; }
    try { await api.put(`${base}/${p.id}`, { ...p, lat: ll.lat, lng: ll.lng, bike_path_id: pid }); await load(); }
    catch { toast.error("Couldn't move pin"); }
  };
  const review = async (p, action) => {
    const reason = action === "reject" ? window.prompt("Reason for not approving (shown to the partner):", "") : "";
    if (reason === null) return;
    try { await api.post(`/admin/amenities/${p.id}/${action}`, action === "reject" ? { reason } : {}); toast.success(action === "approve" ? "Pin approved — partner notified" : "Pin not approved — partner notified"); await load(); loadPending(); }
    catch { toast.error("Couldn't update the pin"); }
  };

  return (
    <div className="space-y-4" data-testid="trailheads-admin-panel">
      <div className="flex flex-col sm:flex-row sm:items-end gap-3 justify-between">
        <div>
          <h2 className="font-heading font-bold text-xl">Trailheads & Amenities</h2>
          <p className="text-sm text-muted-foreground">{partner
            ? "Click inside your assigned zone (shaded on the map) to suggest a trailhead, parking or restroom pin. Velopaths reviews each pin before riders see it."
            : "Click the map to drop a pin. Mark one trailhead as Primary — riders' \"Show drive to trailhead\" route goes there first. Partner pins wait for your approval."}</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          {!partner && <Select value={stateF} onValueChange={pickState}>
            <SelectTrigger className="sm:w-48" data-testid="trailheads-state-select"><SelectValue placeholder="All states" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All states</SelectItem>
              {states.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              {paths.some((p) => !pathStates[p.id]) && <SelectItem value="none">No state set</SelectItem>}
            </SelectContent>
          </Select>}
          <Select value={pid} onValueChange={setPid}>
            <SelectTrigger className="sm:w-72" data-testid="trailheads-path-select"><SelectValue placeholder={shownPaths.length ? "Choose a bike path" : partner ? "Lease a zone to add pins" : "No bike paths in this state"} /></SelectTrigger>
            <SelectContent>{shownPaths.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      {!partner && <PendingStrip items={pending} onOpen={jumpTo} />}
      <div className="grid lg:grid-cols-12 gap-4">
        <div className="lg:col-span-8">
          <LeafletMap height={560} onClick={mapClick}>
            <FitBounds bounds={bounds} nonce={pid} />
            {zones.map((z, i) => <Polygon key={`z${i}`} positions={z.polygon} pathOptions={{ color: "#22D3EE", weight: 2, fillOpacity: 0.12, dashArray: "6 4" }} interactive={false} />)}
            {lines.map((ln, i) => <Polyline key={i} positions={ln} pathOptions={{ color: "#16A34A", weight: 5 }} />)}
            {pins.map((p) => (
              <Marker key={p.id} position={[p.lat, p.lng]} icon={pinIcon(p)} draggable={canEdit(p)}
                eventHandlers={{ click: () => canEdit(p) ? setEdit(p) : toast.info(`${p.name} — added by Velopaths`), dragend: (e) => move(p, e.target.getLatLng()) }} />
            ))}
          </LeafletMap>
        </div>
        <div className="lg:col-span-4 space-y-4">
          <PinList pins={pins} onEdit={(p) => canEdit(p) && setEdit(p)} onReview={partner ? null : review} />
          <RiderPreviewList points={riderView} />
        </div>
      </div>
      <TrailheadPinDialog pin={edit} onClose={() => setEdit(null)} onSave={save} onDelete={remove} saving={saving} />
    </div>
  );
}

function PinList({ pins, onEdit, onReview }) {
  return (
    <div className="rounded-xl border border-border bg-card p-3" data-testid="amenity-pin-list">
      <div className="font-heading font-bold text-sm mb-2">Pins on this path ({pins.length})</div>
      {pins.length === 0 && <p className="text-xs text-muted-foreground">No pins yet. Click the map to add a trailhead.</p>}
      <div className="space-y-1.5 max-h-64 overflow-y-auto">
        {pins.map((p) => (
          <div key={p.id} className="rounded-lg hover:bg-secondary transition-colors">
          <button onClick={() => onEdit(p)} data-testid={`amenity-pin-${p.id}`}
            className="w-full flex items-center gap-2 px-2 py-1.5 text-left">
            <span className="w-3 h-3 rounded-full shrink-0" style={{ background: colorOf(p.type) }} />
            <span className="flex-1 min-w-0 text-sm truncate">{p.name}</span>
            {STATUS_BADGE[p.status] && <span className={`text-[10px] rounded-full px-2 py-0.5 ${STATUS_BADGE[p.status][1]}`} data-testid={`amenity-status-${p.id}`}>{STATUS_BADGE[p.status][0]}</span>}
            {p.is_primary && <Badge className="gap-1" data-testid="amenity-primary-badge"><Star className="w-3 h-3" />Primary</Badge>}
          </button>
          {p.status === "rejected" && p.rejection_reason && <p className="px-7 pb-1 text-[11px] text-destructive">{p.rejection_reason}</p>}
          {onReview && p.status === "pending" && (
            <div className="flex gap-1.5 px-7 pb-1.5">
              <button onClick={() => onReview(p, "approve")} className="text-[11px] rounded-full bg-primary text-primary-foreground px-2.5 py-0.5" data-testid={`amenity-approve-${p.id}`}>Approve</button>
              <button onClick={() => onReview(p, "reject")} className="text-[11px] rounded-full border border-border px-2.5 py-0.5" data-testid={`amenity-reject-${p.id}`}>Don't approve</button>
              <span className="text-[10px] text-muted-foreground self-center truncate">by {p.created_by}</span>
            </div>
          )}
          </div>
        ))}
      </div>
    </div>
  );
}

function PendingStrip({ items, onOpen }) {
  if (!items.length) return null;
  return (
    <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-3" data-testid="pending-pins-strip">
      <div className="font-heading font-bold text-sm mb-2 text-amber-500" data-testid="pending-pins-count">{items.length} partner pin{items.length === 1 ? "" : "s"} waiting for approval</div>
      <div className="flex flex-wrap gap-2">
        {items.map((p) => (
          <button key={p.id} onClick={() => onOpen(p)} data-testid={`pending-pin-open-${p.id}`}
            className="text-xs rounded-full border border-border bg-card px-3 py-1 hover:bg-secondary transition-colors text-left">
            <span className="font-semibold">{p.name}</span> · {(AMENITY_TYPES.find((x) => x.value === p.type) || {}).label || p.type} · {p.bike_path_name || "Bike path"}{p.shop_name ? ` · ${p.shop_name}` : ""}
          </button>
        ))}
      </div>
    </div>
  );
}

function RiderPreviewList({ points }) {
  return (
    <div className="rounded-xl border border-border bg-card p-3" data-testid="trailheads-rider-preview">
      <div className="font-heading font-bold text-sm mb-1 flex items-center gap-1.5"><Navigation className="w-4 h-4 text-primary" />Riders are sent to</div>
      <p className="text-[11px] text-muted-foreground mb-2">Order shown without a rider location. With location, each group is sorted nearest-first.</p>
      {points.length === 0 && <p className="text-xs text-muted-foreground">No access point found — add a trailhead pin.</p>}
      <ol className="space-y-1.5">
        {points.map((p, i) => (
          <li key={i} className="flex items-start gap-2 text-sm" data-testid={`rider-preview-point-${i}`}>
            <MapPin className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
            <span><span className="block">{p.name}</span><span className="block text-[11px] text-muted-foreground">{TYPE_LABEL[p.type] || p.type}</span></span>
          </li>
        ))}
      </ol>
    </div>
  );
}
